import { Button, Icon, SkeletonShape } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { AnimatePresence, m } from 'motion/react';
import { useMatch } from '@/services/router/compat';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { useCopilotSessionContext } from '@/features/copilot/components/session-provider';
import { useShouldShowAgentBar } from '../../hooks/use-agent-fullscreen';
import { useSidebarActive } from '@/features/sidebar';
import { useCopilotSidebar } from '@/features/copilot/hooks/use-copilot-sidebar';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
// oxlint-disable-next-line local-rules/prefer-use-mount-effect -- syncs GlobalCSSVariable when isHidden changes
import { useEffect, useId } from 'react';
import { Suggestions, useSuggestions } from '../suggestions';
import * as styles from './index.css';

const ASSISTANT_FOOTER_HEIGHT = '45px';

/** SVG paths for gradient hover layer only (default uses Pluma `Icon` sparkle). */
const SPARKLE_LARGE_PATH =
  'M15.987 9.36 23 12l-7.013 2.639L13.29 21.5l-2.699-6.861L3.58 12l7.012-2.64L13.29 2.5l2.697 6.86Z';
const SPARKLE_SMALL_BOTTOM_PATH = 'M5.189 19.068H3v-1.285h2.19V15.64h1.313v2.142h2.189v1.285h-2.19v2.142H5.19v-2.142Z';
const SPARKLE_SMALL_TOP_PATH = 'M5.19 6.217H3V4.932h2.19V2.79h1.313v2.142h2.189v1.285h-2.19V8.36H5.19V6.217Z';

const TRANSITION = { duration: 0.2, ease: [0.2, 0, 0.38, 0.9] } as const;

/** Matches command-dialog “Ask Agent” row (`command-dialog.tsx`). */
const askAgentSparkleIconProps = {
  name: 'sparkle',
  size: 'sm',
  sizeVersion: 'v2',
  color: 'base',
} as const;

function AskAgentSparkleIcon() {
  const id = useId();
  const gradientId = `assistant-footer-sparkle-gradient-${id.replace(/:/g, '')}`;

  return (
    <span className={styles.sparkleWrap} aria-hidden>
      <span className={styles.sparklePluma}>
        <Icon {...askAgentSparkleIconProps} />
      </span>
      <svg className={styles.sparkleGradient} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={gradientId} x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" style={{ stopColor: themeVars.palette['orange-400'] }} />
            <stop offset="100%" style={{ stopColor: themeVars.palette['purple-500'] }} />
          </linearGradient>
        </defs>
        <path d={SPARKLE_LARGE_PATH} fill={`url(#${gradientId})`} />
        <path d={SPARKLE_SMALL_TOP_PATH} fill={`url(#${gradientId})`} />
        <path d={SPARKLE_SMALL_BOTTOM_PATH} fill={`url(#${gradientId})`} />
      </svg>
    </span>
  );
}

type AskAssistantButtonProps = { onClick: () => void };

function AskAssistantButton({ onClick }: AskAssistantButtonProps) {
  return (
    <GradientRing radius={4}>
      <Button variant="tertiary" size="sm" className={gradientHoverTarget} onClick={onClick}>
        <AskAgentSparkleIcon />
        Ask Agent
      </Button>
    </GradientRing>
  );
}

export function AssistantFooter() {
  const isCopilotSidebarOpen = useSidebarActive('copilot');
  const shouldShowAgentBar = useShouldShowAgentBar();
  const session = useCopilotSessionContext();
  const suggestionState = useSuggestions(session);
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const themeContext = useThemeContext();
  const isHomePage = useMatch('/workspaces/:workspaceId/home') !== null;

  const isHidden = isCopilotSidebarOpen || !shouldShowAgentBar || isHomePage;

  useEffect(() => {
    themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, isHidden ? '0px' : ASSISTANT_FOOTER_HEIGHT);

    return () => {
      themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, '0px');
    };
  }, [isHidden, themeContext]);

  if (isHidden) {
    return null;
  }

  const handleSuggestionClick = async (message: string, index: number) => {
    try {
      openCopilot({ triggerSource: 'footer_suggestion' });
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'footer',
        'Conversation ID': session.id,
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  const handleAskAssistant = () => {
    openCopilot({ triggerSource: 'footer' });
  };

  return (
    <div className={styles.footer}>
      <div className={styles.suggestionsContainer}>
        <AnimatePresence mode="popLayout">
          {suggestionState.isVisible
            ? suggestionState.suggestions.map((suggestion, index) => (
                <m.div
                  key={suggestion.label}
                  layout
                  initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  transition={{ ...TRANSITION, delay: index * 0.05 }}
                >
                  <Suggestions
                    suggestions={[suggestion]}
                    onSuggestionClick={(message) => handleSuggestionClick(message, index + 1)}
                  />
                </m.div>
              ))
            : [140, 120, 160].map((width, index) => (
                <m.div
                  key={`skeleton-${index}`}
                  layout
                  initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  transition={{ ...TRANSITION, delay: index * 0.05 }}
                >
                  <SkeletonShape style={{ width, height: 29, borderRadius: themeVars.radius['4'] }} />
                </m.div>
              ))}
        </AnimatePresence>
      </div>
      <div className={styles.actionsContainer}>
        <AskAssistantButton onClick={handleAskAssistant} />
      </div>
    </div>
  );
}
